import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FASTENER_TYPES = [
  "Stud Nuts & Heavy Hex Nuts",
  "Heavy Hex Bolts",
  "Threaded Rods",
  "Washers",
  "Custom Machined Components",
  "Full Package / Tender",
];

const inputCls =
  "w-full bg-white border border-steel-light rounded-md text-ink font-sans text-sm px-4 py-3 outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-all duration-200 placeholder:text-slate-400";

const labelCls =
  "block font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-steel mb-2";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    fastener_type: FASTENER_TYPES[0],
    quantity: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setForm((f) => ({
        ...f,
        fastener_type: FASTENER_TYPES[0],
        message: e.detail,
      }));
    window.addEventListener("rew:prefill-rfq", onPrefill);
    return () => window.removeEventListener("rew:prefill-rfq", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("RFQ received — our team will get back to you within one working day.");
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        fastener_type: FASTENER_TYPES[0],
        quantity: "",
        message: "",
      });
    } catch {
      toast.error("Could not submit right now — please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-navy-deep text-white py-20 lg:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Left — details */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 font-sans font-medium text-[11px] sm:text-xs tracking-[0.12em] uppercase text-brand font-semibold mb-4">
            <span className="w-10 h-[2px] bg-brand" />
            Contact & RFQ
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl">
            Request a quotation
          </h2>
          <p className="mt-5 max-w-md text-sm sm:text-base text-slate-400 leading-relaxed">
            Send your drawing, grade or quantity — we quote fast and manufacture
            to spec. Direct lines below reach the shop floor, not a call centre.
          </p>

          <div className="mt-10 space-y-7">
            <div className="flex gap-4" data-testid="contact-phones">
              <span className="shrink-0 w-10 h-10 border border-slate-700 flex items-center justify-center text-brand">
                <Phone size={16} />
              </span>
              <div>
                <div className="font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-steel mb-1.5">
                  Call us
                </div>
                <div className="text-sm leading-relaxed">
                  Manish R Shah —{" "}
                  <a href="tel:+918000151777" className="hover:text-brand transition-colors" data-testid="contact-phone-link-1">
                    8000151777
                  </a>{" "}
                  /{" "}
                  <a href="tel:+919375267221" className="hover:text-brand transition-colors" data-testid="contact-phone-link-2">
                    9375267221
                  </a>
                  <br />
                  Rahil Shah —{" "}
                  <a href="tel:+918905853080" className="hover:text-brand transition-colors" data-testid="contact-phone-link-3">
                    8905853080
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-4" data-testid="contact-emails">
              <span className="shrink-0 w-10 h-10 border border-slate-700 flex items-center justify-center text-brand">
                <Mail size={16} />
              </span>
              <div>
                <div className="font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-steel mb-1.5">
                  Email
                </div>
                <div className="text-sm leading-relaxed">
                  <a href="mailto:sales@rahilengineering.com" className="hover:text-brand transition-colors" data-testid="contact-email-link-1">
                    sales@rahilengineering.com
                  </a>
                  <br />
                  <a href="mailto:info@rahilengineering.com" className="hover:text-brand transition-colors" data-testid="contact-email-link-2">
                    info@rahilengineering.com
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-4" data-testid="contact-address">
              <span className="shrink-0 w-10 h-10 border border-slate-700 flex items-center justify-center text-brand">
                <MapPin size={16} />
              </span>
              <div>
                <div className="font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-steel mb-1.5">
                  Works
                </div>
                <div className="text-sm leading-relaxed">
                  Rahil Engineering Works
                  <br />
                  303/22 Makarpura GIDC, Vadodara, Gujarat, India
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right — RFQ form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          className="bg-navy-surface/60 border border-slate-800 p-6 sm:p-8 h-fit"
          data-testid="rfq-form"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display font-extrabold uppercase text-2xl">
              Request a Quote
            </h3>
            <span className="font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-brand border border-brand/40 px-2.5 py-1">
              RFQ Form
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="rfq-name">Full Name *</label>
              <input
                id="rfq-name"
                required
                value={form.name}
                onChange={set("name")}
                className={inputCls}
                placeholder="e.g. Robert Sterling"
                data-testid="rfq-input-name"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rfq-company">Company</label>
              <input
                id="rfq-company"
                value={form.company}
                onChange={set("company")}
                className={inputCls}
                placeholder="e.g. PetroChem Global"
                data-testid="rfq-input-company"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rfq-email">Email *</label>
              <input
                id="rfq-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                placeholder="you@company.com"
                data-testid="rfq-input-email"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rfq-phone">Phone / WhatsApp</label>
              <input
                id="rfq-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
                placeholder="+91 ..."
                data-testid="rfq-input-phone"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="rfq-type">Fastener Category</label>
              <select
                id="rfq-type"
                value={form.fastener_type}
                onChange={set("fastener_type")}
                className={inputCls}
                data-testid="rfq-input-fastener-type"
              >
                {FASTENER_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="rfq-quantity">Quantity / Weight</label>
              <input
                id="rfq-quantity"
                value={form.quantity}
                onChange={set("quantity")}
                className={inputCls}
                placeholder='e.g. 5,000 pcs / 2.5 MT'
                data-testid="rfq-input-quantity"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="rfq-message">Specification / Project Details</label>
              <textarea
                id="rfq-message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                className={inputCls}
                placeholder="Dimensions, grade (e.g. ASTM A193 B7), coating and delivery timeline..."
                data-testid="rfq-input-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-rfq-btn"
            className="mt-6 w-full bg-brand text-white font-sans font-medium text-xs uppercase tracking-[0.14em] font-extrabold py-4 rounded-md flex items-center justify-center gap-2 hover:bg-navy-deep transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? (
              <>
                <Loader2 size={15} className="animate-spin" /> Submitting...
              </>
            ) : (
              <>
                Submit RFQ <ArrowUpRight size={15} />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
